import React, { useEffect, useState } from 'react';
import { Add, Delete } from '@mui/icons-material';
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { ServiceTypeMappingFragmentProps } from './ServiceTypeMappingFragment.props';
import { Accordian } from '../../../../components/atoms/Accordian';
import { Button, ButtonVariantProps } from '../../../../components/atoms/Button';
import { ColorProps, ServiceCommonType, ServiceTypeMappingType } from '../../../../@types';
import { SAMPLE_SERVICE_COMMON_TYPE } from '../../../../constants/service.constant';
import { useGetServiceActionProcessTypes } from '../../../../queries/service-query';
import { GRID_SPACING } from '../../../../configs/ui-constants';
import { ToogleButtonTextAlignment } from '../../../../components/atoms/ToogleButtonGroup';
import { SelectMenu } from '../../../../components/atoms/SelectMenu';
import { actionTypes, processTypes, serviceTypes } from '../../../../utils/services';
import { SwitchGrid } from './ServiceTypeMappingFragment.styles';
import { Switch } from '../../../../components/atoms/Switch';
import { FormControlLabelPlacementProps } from '../../../../components/atoms/FormControlLabel';
import { TextField } from '../../../../components/atoms/TextField';
import { EXPLICIT_SERVICE_TYPE_MAPPINGS } from '../../../../constants/structure';
import { IconButtonColor, IconFontSize } from '../../../../configs/enums';
import { ConfirmationPopup } from '../../../../components/molecules/ConfirmationPopup';

export const ServiceTypeMappingFragment: React.FC<ServiceTypeMappingFragmentProps> = ({
  serviceTypeMappings,
  onChange,
  error
}) => {
  const activeOnly = true;
  const [expanded, setExpanded] = useState(true);
  const [serviceCommonTypes, setServiceCommonTypes] = useState<ServiceCommonType>(SAMPLE_SERVICE_COMMON_TYPE);
  const [modalStatus, setModalStatus] = useState<{open: boolean; data: ServiceTypeMappingType}>({ open: false, data: EXPLICIT_SERVICE_TYPE_MAPPINGS[0] });

  const serviceActionProcessTypesQuery = useGetServiceActionProcessTypes(activeOnly);

  useEffect(() => {
    serviceActionProcessTypesQuery.data && setServiceCommonTypes(serviceActionProcessTypesQuery.data);
  }, [serviceActionProcessTypesQuery.data]);

  const handleAdd = (event: any) => {
    event.stopPropagation();
    const updatedDataArray = [...serviceTypeMappings];
    updatedDataArray.push({
      ...EXPLICIT_SERVICE_TYPE_MAPPINGS[0],
      mapId: serviceTypeMappings.length > 0 ? Math.max(...serviceTypeMappings.map((data) => data.mapId)) + 1 : 0
    });
    onChange(updatedDataArray);
  };

  const handleDelete = () => {
    const updatedDataArray = [...serviceTypeMappings].filter((serviceTypeMapping) => serviceTypeMapping.mapId !== modalStatus.data.mapId);
    onChange(serviceTypeMappings.length > 0 ? updatedDataArray : []);
    setModalStatus({ open: false, data:  EXPLICIT_SERVICE_TYPE_MAPPINGS[0] });
  };

  const updateData = (mapId: number, fieldData: ServiceTypeMappingType, fieldName: string, selectedValue: any) => {
    const serviceTypeMappingArray = [...serviceTypeMappings];
    const updatedArray = serviceTypeMappingArray.map((serviceTypeMapping) => 
      serviceTypeMapping.mapId === mapId ? {
        ...fieldData,
        [fieldName]: selectedValue
      } : serviceTypeMapping
    );
    onChange(updatedArray);
  };

  return (
    <>
      <Accordian
        title={
          <Grid container justifyContent="space-between" width="100%">
            <Grid mr={2}>
              <Typography>Service Type Mappings</Typography>
            </Grid>
            <Grid mr={2}>
              <Button variant={ButtonVariantProps.Secondary} startIcon={<Add />} onClick={handleAdd}>
                  Add
              </Button>
            </Grid>
          </Grid>
        }
        onChange={(_, newExpanded: boolean) => setExpanded(newExpanded)}
        expanded={expanded}
      >
        {serviceTypeMappings?.map((serviceTypeMapping) =>
          <Stack key={serviceTypeMapping.mapId} direction="row" spacing={GRID_SPACING} alignItems={ToogleButtonTextAlignment.Center}>
            <TextField
              required
              label="Mapped Name"
              value={serviceTypeMapping.mappedName}
              onChange={(mappedName: string) => updateData(serviceTypeMapping.mapId, serviceTypeMapping, 'mappedName', mappedName)}
              error={serviceTypeMapping.mappedName ? false : error}
            />
            <SelectMenu
              id="service-type"
              label="Service Type*"
              minWidth={200}
              items={serviceTypes(serviceCommonTypes.serviceTypes)}
              selectedValue={serviceTypeMapping.selectedServiceType}
              onChange={(serviceType: string) => updateData(serviceTypeMapping.mapId, serviceTypeMapping, 'selectedServiceType', serviceType)}
              validate={error}
              helperTextEnabled={false}
            />
            <SelectMenu
              id="action-type"
              label="Action Type*"
              minWidth={200}
              items={actionTypes(serviceCommonTypes.actionTypes)}
              selectedValue={serviceTypeMapping.selectedActionType}
              onChange={(actionType: string) => updateData(serviceTypeMapping.mapId, serviceTypeMapping, 'selectedActionType', actionType)}
              validate={error}
              helperTextEnabled={false}
            />
            <SelectMenu
              id="process-type"
              label="Process Type*"
              minWidth={200}
              items={processTypes(serviceCommonTypes.processTypes)}
              selectedValue={serviceTypeMapping.selectedProcessType}
              onChange={(processType: string) => updateData(serviceTypeMapping.mapId, serviceTypeMapping, 'selectedProcessType', processType)}
              validate={error}
              helperTextEnabled={false}
            />
            <SwitchGrid>
              <FormControlLabel
                control={
                  <Switch
                    color={ColorProps.Success}
                    onChange={(event) => updateData(serviceTypeMapping.mapId, serviceTypeMapping, 'isAvailable', event.target.checked)}
                    checked={serviceTypeMapping.isAvailable} />
                }
                label="Available to Customer Users"
                labelPlacement={FormControlLabelPlacementProps.Top}
              />
            </SwitchGrid>
            <IconButton color={IconButtonColor.Error} onClick={() => setModalStatus({ open: true, data: serviceTypeMapping })}>
              <Delete fontSize={IconFontSize.Large} />
            </IconButton>
          </Stack>
        )}
      </Accordian>
      <ConfirmationPopup
        open={modalStatus.open}
        mainHeader="Warning"
        description="Are you sure you want to remove this Mapping?"
        leftButtonName="Cancel"
        rightButtonName="Remove"
        leftButtonOnClick={() => setModalStatus({ open: false, data: EXPLICIT_SERVICE_TYPE_MAPPINGS[0] })}
        rightButtonOnClick={handleDelete}
        leftButtonVariant={ButtonVariantProps.Secondary}
      />
    </>
  );
};